import type { ApiClient, IApiJsonRequest } from '../../ApiClient';
import type {
  IApiCreateArticleDraftRequestBody,
  IApiCreateArticleDraftResponseBody,
  IApiCreateArticleRequestBody,
  IApiCreateArticleResponseBody,
  IApiDeleteArticleResponseBody,
  IApiGenerateArticleShareUrlResponseBody,
  IApiGetArticleDraftResponseBody,
  IApiGetArticleLikesViewsResponseBody,
  IApiGetArticleListQueryBody,
  IApiGetArticleListResponseBody,
  IApiGetArticleResponseBody,
  IApiPublishArticleRequestBody,
  IApiPublishArticleResponseBody,
  IApiSearchArticlesQueryBody,
  IApiSearchArticlesResponseBody,
  IApiUnpublishArticleResponseBody,
} from './ArticlesApi.types';

export class ArticlesApi {
  public constructor(private client: ApiClient) {}

  public async getAll(
    appId: string,
    body?: IApiGetArticleListQueryBody,
  ): Promise<IApiGetArticleListResponseBody> {
    const request: IApiJsonRequest<IApiGetArticleListQueryBody> = {
      path: '/admin/press/articlesAll',
      method: 'get',
      params: body,
    };

    const response =
      await this.client.fetchJson<IApiGetArticleListResponseBody>(request, {
        appId,
      });

    return response.body;
  }

  public async get(
    appId: string,
    articleId: string,
  ): Promise<IApiGetArticleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/press/articles/${articleId}`,
    };

    const response = await this.client.fetchJson<IApiGetArticleResponseBody>(
      request,
      { appId },
    );

    return response.body;
  }

  public async getDraft(
    appId: string,
    articleId: string,
  ): Promise<IApiGetArticleDraftResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/press/articles/${articleId}/draft`,
    };

    const response =
      await this.client.fetchJson<IApiGetArticleDraftResponseBody>(request, {
        appId,
      });

    return response.body;
  }

  public async create(
    appId: string,
    body: IApiCreateArticleRequestBody,
  ): Promise<IApiCreateArticleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'post',
      path: '/press/articles',
      body,
    };

    const response = await this.client.fetchJson<IApiCreateArticleResponseBody>(
      request,
      { appId },
    );

    return response.body;
  }

  public async createDraft(
    appId: string,
    articleId: string,
    body: IApiCreateArticleDraftRequestBody,
  ): Promise<IApiCreateArticleDraftResponseBody> {
    const request: IApiJsonRequest = {
      method: 'put',
      path: '/press/articles',
      body: {
        ...body,
        articleId,
      },
    };

    const response =
      await this.client.fetchJson<IApiCreateArticleDraftResponseBody>(request, {
        appId,
      });

    return response.body;
  }

  public async publish(
    appId: string,
    articleId: string,
    body: IApiPublishArticleRequestBody,
  ): Promise<IApiPublishArticleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'put',
      path: `/press/articles/${articleId}/publish`,
      body,
    };

    const response =
      await this.client.fetchJson<IApiPublishArticleResponseBody>(request, {
        appId,
      });

    return response.body;
  }

  public async unpublish(
    appId: string,
    articleId: string,
  ): Promise<IApiUnpublishArticleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'put',
      path: `/press/articles/${articleId}/unpublish`,
      body: {
        articleId,
      },
    };

    const response =
      await this.client.fetchJson<IApiUnpublishArticleResponseBody>(request, {
        appId,
      });

    return response.body;
  }

  public async delete(
    appId: string,
    articleId: string,
  ): Promise<IApiDeleteArticleResponseBody> {
    const request: IApiJsonRequest = {
      method: 'delete',
      path: `/press/articles/${articleId}`,
      body: {
        articleId,
      },
    };

    const response = await this.client.fetchJson<IApiDeleteArticleResponseBody>(
      request,
      { appId },
    );

    return response.body;
  }

  public async search(
    appId: string,
    body?: IApiSearchArticlesQueryBody,
  ): Promise<IApiSearchArticlesResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: '/press/atlasSearch',
      params: {
        ...body,
        allFields: true,
      },
    };

    const data =
      await this.client.fetchJsonStandardized<IApiSearchArticlesResponseBody>(
        request,
        { appId },
      );

    return data;
  }

  public async generateShareUrl(
    appId: string,
    articleId: string,
  ): Promise<IApiGenerateArticleShareUrlResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/press/articles/${articleId}/shareUrl`,
    };

    const document =
      await this.client.fetchJson<IApiGenerateArticleShareUrlResponseBody>(
        request,
        { appId },
      );

    return document.body;
  }

  public async getLikesViews(
    appId: string,
    articleId: string,
  ): Promise<IApiGetArticleLikesViewsResponseBody> {
    const request: IApiJsonRequest = {
      method: 'get',
      path: `/press/articles/${articleId}/reactions?types=#total,#views`,
    };

    const response =
      await this.client.fetchJson<IApiGetArticleLikesViewsResponseBody>(
        request,
        { appId },
      );

    return response.body;
  }
}
